import { datadogRum } from '@datadog/browser-rum';

import { baseUrl } from '@/consts/route';
import { reportClientLog, serializeError } from '@/lib/client-log';

// --- Types ---

export interface GrsTokenData {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresIn: number;
  refreshTokenExpiresIn: number;
  user: Record<string, any>;
}

type Listener = (data: GrsTokenData | null) => void;

// --- Config ---

/** Supabase 패턴: 단일 키에 세션 객체를 JSON으로 저장 */
export const GRS_SESSION_STORAGE_KEY = 'grs.session';

/** accessToken 만료 전 갱신 여유 시간 */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/** backoffRetry 최대 재시도 횟수 */
const MAX_RETRY = 5;

/** backoffRetry 초기 대기 시간 (ms) */
const INITIAL_BACKOFF = 500;

// --- Storage ---

function readStorage(): GrsTokenData | null {
  try {
    const raw = sessionStorage.getItem(GRS_SESSION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as GrsTokenData;
    if (!parsed.accessToken || !parsed.refreshToken) return null;

    return parsed;
  } catch {
    return null;
  }
}

// iframe + 구버전 브라우저 조합에서 sessionStorage write가 SecurityError를 throw하는 케이스 방어.
// 이 throw가 useLottegrsGateway의 catch-all로 흘러가 "유효기간 만료" 오탐을 일으켰던 근본 원인.
// fallback은 in-memory: 같은 탭 내비게이션은 가능, 새로고침 시엔 세션 유실 → 매직링크 재진입.
function writeStorage(data: GrsTokenData): boolean {
  try {
    sessionStorage.setItem(GRS_SESSION_STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (err) {
    const wrapped = err instanceof Error ? err : new Error('grs sessionStorage write failed');
    // Datadog는 storage 차단 환경에서 자체 세션이 안 만들어져 전송 실패할 수 있음 → BE 우회 채널 병행
    datadogRum.addError(wrapped, { context: 'grs-token-manager.writeStorage' });
    reportClientLog({
      context: 'grs-token-manager.writeStorage',
      error: serializeError(err),
    });
    return false;
  }
}

function clearStorage(): boolean {
  try {
    sessionStorage.removeItem(GRS_SESSION_STORAGE_KEY);
    return true;
  } catch (err) {
    const wrapped = err instanceof Error ? err : new Error('grs sessionStorage clear failed');
    datadogRum.addError(wrapped, { context: 'grs-token-manager.clearStorage' });
    reportClientLog({
      context: 'grs-token-manager.clearStorage',
      error: serializeError(err),
    });
    return false;
  }
}

// --- API ---

/** POST /auth/refresh with exponential backoff */
async function fetchRefresh(refreshToken: string): Promise<GrsTokenData | null> {
  for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
    try {
      const res = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken, type: 'lt' }),
      });

      if (res.status === 401 || res.status === 403) return null;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      return await res.json();
    } catch {
      if (attempt === MAX_RETRY) return null;
      await new Promise((r) => setTimeout(r, INITIAL_BACKOFF * Math.pow(2, attempt)));
    }
  }
  return null;
}

/** GRS 토큰 생명주기 관리자 (React 독립) */
export class GrsTokenManager {
  private data: GrsTokenData | null = null;
  private listeners = new Set<Listener>();
  private revalidatingListeners = new Set<(v: boolean) => void>();

  // exhaustMapWithTrailing: Promise 기반 (refreshingRef + pendingRef 대체)
  private inflight: Promise<void> | null = null;
  private trailing = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.data = readStorage();
    }
  }

  // --- Public API ---

  get current() {
    return this.data;
  }

  get isAuthenticated() {
    return !!this.data?.accessToken;
  }

  signIn(data: GrsTokenData) {
    writeStorage(data);
    this.emit(data);
  }

  signOut() {
    clearStorage();
    this.emit(null);
  }

  /**
   * 토큰 갱신
   *
   * exhaustMapWithTrailing 동작:
   * - 진행 중이 아니면 즉시 실행
   * - 진행 중이면 trailing 플래그만 세팅
   * - 실행 완료 후 trailing이 있으면 1번 더 실행
   */
  revalidate = () => {
    if (this.inflight) {
      this.trailing = true;
      return;
    }

    this.inflight = this.performRevalidation().finally(() => {
      this.inflight = null;

      if (this.trailing) {
        this.trailing = false;
        this.revalidate();
      }
    });
  };

  /** 구독: 토큰 데이터 변경 시 호출 */
  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** 구독: revalidating 상태 변경 시 호출 */
  onRevalidating(listener: (v: boolean) => void) {
    this.revalidatingListeners.add(listener);
    return () => {
      this.revalidatingListeners.delete(listener);
    };
  }

  // --- Private ---

  private async performRevalidation() {
    const current = this.data;
    if (!current?.accessToken) return;

    // refreshToken 만료 체크
    if (current.refreshTokenExpiresIn && Date.now() >= current.refreshTokenExpiresIn) {
      this.handleExpired();
      return;
    }

    // accessToken 아직 유효 → 스킵
    if (!current.accessTokenExpiresIn) return;
    if (current.accessTokenExpiresIn - Date.now() >= REFRESH_BUFFER_MS) return;

    this.emitRevalidating(true);

    const newTokens = await fetchRefresh(current.refreshToken);

    this.emitRevalidating(false);

    if (!newTokens) {
      this.handleExpired();
      return;
    }

    const updated: GrsTokenData = {
      accessToken: newTokens.accessToken || current.accessToken,
      refreshToken: newTokens.refreshToken || current.refreshToken,
      accessTokenExpiresIn: newTokens.accessTokenExpiresIn || current.accessTokenExpiresIn,
      refreshTokenExpiresIn: newTokens.refreshTokenExpiresIn || current.refreshTokenExpiresIn,
      user: current.user,
    };

    // isEqual: 토큰 또는 만료시간이 바뀌었을 때만 알림
    const changed =
      this.data?.accessToken !== updated.accessToken ||
      this.data?.refreshToken !== updated.refreshToken ||
      this.data?.accessTokenExpiresIn !== updated.accessTokenExpiresIn;

    if (changed) {
      writeStorage(updated);
      this.emit(updated);
    }
  }

  private handleExpired() {
    clearStorage();
    this.emit(null);
  }

  private emit(data: GrsTokenData | null) {
    this.data = data;
    this.listeners.forEach((fn) => fn(data));
  }

  private emitRevalidating(value: boolean) {
    this.revalidatingListeners.forEach((fn) => fn(value));
  }
}

/** 싱글턴 인스턴스 */
export const grsTokenManager = new GrsTokenManager();

/** E2E 테스트용: window에서 접근 가능하도록 노출 */
if (typeof window !== 'undefined') {
  (window as any).__grsTokenManager = grsTokenManager;
}
